import { useCallback, useRef, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { IDocumentData } from '@models/document/IDocumentData';
import { ITab } from '@srm/ui/types/ITab';
import { IDocumentTab } from './tabs/DocumentTab.types';

/** Ошибка контрола без записи в `doc.data.fields` (блок вложений): вкладка + DOM-якорь. */
export interface IErrorAnchor {
    anchorId: string;
    documentTabId: string;
}

export interface IErrorTabRequest {
    nonce: number;
    errorIds: number[];
    documentTabIds?: Record<number, string>;
    anchors?: IErrorAnchor[];
}

export interface IErrorTabTarget {
    value: string;
    fieldIds: number[];
    anchorIds: string[];
}

export interface IActiveErrorTabRequest extends IErrorTabTarget {
    nonce: number;
}

export const getDocumentErrorFieldIds = (errors: FieldErrors<IDocumentData>): number[] => {
    if (!errors.fields) return [];

    return Object.keys(errors.fields)
        .map(Number)
        .filter((id) => Number.isInteger(id) && id >= 0);
};

export const getDocumentErrorTabIds = (errors: FieldErrors<IDocumentData>): Record<number, string> => {
    const result: Record<number, string> = {};
    Object.entries(errors.fields ?? {}).forEach(([rawId, error]) => {
        const id = Number(rawId);
        const documentTabId = (error as { value?: { documentTabId?: unknown } } | undefined)?.value?.documentTabId;
        if (Number.isInteger(id) && id >= 0 && typeof documentTabId === 'string') result[id] = documentTabId;
    });
    return result;
};

/**
 * Ошибочное поле относится к табу, если у индекса нет точного `documentTabId` (index встречается
 * в одном табе) либо этот id совпадает со значением таба (index в нескольких табах — resolver
 * уточняет принадлежность). Единое правило для навигации и подсветки невалидных вкладок.
 */
export const errorFieldMatchesTab = (
    fieldId: number,
    tabValue: string | undefined,
    documentTabIds: Record<number, string>,
): boolean => !documentTabIds[fieldId] || documentTabIds[fieldId] === tabValue;

export const findFirstErrorTabTarget = (
    visibleTabs: ITab[],
    errorIds: number[],
    documentTabIds: Record<number, string> = {},
    anchors: readonly IErrorAnchor[] = [],
): IErrorTabTarget | undefined => {
    const errorIdSet = new Set(errorIds);

    for (const tab of visibleTabs) {
        const { includedFields } = tab as IDocumentTab;
        if (!tab.value || !Array.isArray(includedFields)) continue;

        const fieldIds = includedFields.filter(
            (id) => errorIdSet.has(id) && errorFieldMatchesTab(id, tab.value, documentTabIds),
        );
        const anchorIds = anchors.filter((anchor) => anchor.documentTabId === tab.value).map(({ anchorId }) => anchorId);
        if (fieldIds.length > 0 || anchorIds.length > 0) return { value: tab.value, fieldIds, anchorIds };
    }

    return undefined;
};

export const resolveFirstInvalidTabRequest = (
    visibleTabs: ITab[] | undefined,
    request: IErrorTabRequest | undefined,
    validationMode: boolean,
): IActiveErrorTabRequest | undefined => {
    if (!validationMode || !visibleTabs || !request) return undefined;

    const target = findFirstErrorTabTarget(
        visibleTabs,
        request.errorIds,
        request.documentTabIds,
        request.anchors,
    );
    return target ? { ...target, nonce: request.nonce } : undefined;
};

/**
 * Ошибки контролов, адресуемых DOM-якорем, а не индексом поля (блок вложений):
 * resolver пишет их по собственному пути формы с `documentAnchorId`/`documentTabId`.
 */
export const getDocumentErrorAnchors = (errors: FieldErrors<IDocumentData>): IErrorAnchor[] =>
    Object.values(errors as Record<string, unknown>).flatMap((error) => {
        const { documentAnchorId, documentTabId } =
            (error as { documentAnchorId?: unknown; documentTabId?: unknown } | undefined) ?? {};
        return typeof documentAnchorId === 'string' && typeof documentTabId === 'string'
            ? [{ anchorId: documentAnchorId, documentTabId }]
            : [];
    });

/** Создаёт повторяемую команду перехода к первому табу с RHF-ошибкой. */
export const useFirstInvalidTabRequest = () => {
    const nonceRef = useRef(0);
    const [errorTabRequest, setErrorTabRequest] = useState<IErrorTabRequest>();

    const requestFirstInvalidTab = useCallback((errors: FieldErrors<IDocumentData>) => {
        const errorIds = getDocumentErrorFieldIds(errors);
        const anchors = getDocumentErrorAnchors(errors);
        if (errorIds.length === 0 && anchors.length === 0) return;

        const documentTabIds = getDocumentErrorTabIds(errors);
        setErrorTabRequest({
            nonce: ++nonceRef.current,
            errorIds,
            ...(Object.keys(documentTabIds).length > 0 ? { documentTabIds } : {}),
            ...(anchors.length > 0 ? { anchors } : {}),
        });
    }, []);

    const completeFirstInvalidTabRequest = useCallback((nonce: number) => {
        setErrorTabRequest((current) => (current?.nonce === nonce ? undefined : current));
    }, []);

    /**
     * Безусловный сброс висящего запроса. Нужен при смене документа/выходе из edit:
     * не резолвящийся сейчас запрос (все ошибочные поля на невидимых вкладках) иначе живёт
     * вечно и «выстреливает» позже — в т.ч. на другом документе, где errorIds (маленькие
     * целые индексы) почти наверняка пересекутся с чужими вкладками.
     */
    const resetFirstInvalidTabRequest = useCallback(() => {
        setErrorTabRequest(undefined);
    }, []);

    return { errorTabRequest, requestFirstInvalidTab, completeFirstInvalidTabRequest, resetFirstInvalidTabRequest };
};
